import { useCallback, useEffect, useState } from "react";

import { formatInt, formatUsd } from "@kontextwerk/web-common";

import FilterBar from "../components/FilterBar";
import PendingCostNotice from "../components/PendingCostNotice";
import LayerPieChart from "../components/LayerPieChart";
import StackedBreakdownChart from "../components/StackedBreakdownChart";
import { fetchCostSummary } from "../lib/api";
import { defaultRange, type DateRange } from "../lib/dateRange";
import type { CostFilters, CostSummaryResponse } from "../lib/types";

export default function PacksRoute() {
  const [range, setRange] = useState<DateRange>(defaultRange(30));
  const [filters, setFilters] = useState<CostFilters>({});
  const [summary, setSummary] = useState<CostSummaryResponse | null>(null);
  const [layerSummary, setLayerSummary] = useState<CostSummaryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Zwei Achsen nebeneinander: pro-Pack (nur L3/L4-Ops tragen pack_id) UND pro-Layer.
      // L1-Embedding + L2-Extraction sind realm-weit (kein pack_id) → erscheinen pro-Pack
      // als '<unassigned>'; die Layer-Sicht macht diesen dominanten Backend-Posten sichtbar.
      const [packResp, layerResp] = await Promise.all([
        fetchCostSummary(range.from, range.to, "pack", filters),
        fetchCostSummary(range.from, range.to, "layer", filters),
      ]);
      setSummary(packResp);
      setLayerSummary(layerResp);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, filters]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-medium">Kosten pro Pack</h1>
        <span className="text-xs text-kw-text-faint">
          {range.from} → {range.to}
        </span>
      </header>
      <FilterBar
        range={range}
        onRangeChange={setRange}
        filters={filters}
        onFiltersChange={setFilters}
        showLayerFilter
      />
      {error ? (
        <div className="p-3 bg-red-900/30 border border-red-700/50 rounded text-sm text-red-300">
          {error}
        </div>
      ) : null}

      <PendingCostNotice pending={summary?.pending} />

      <section className="bg-kw-bg-elevated border border-kw-border-subtle rounded p-4">
        <h2 className="text-sm font-medium text-kw-text-muted mb-3">
          Kosten pro Layer (L1/L2/L3/L4)
        </h2>
        {loading && !layerSummary ? (
          <p className="text-sm text-kw-text-faint">Lade…</p>
        ) : (
          <LayerPieChart buckets={layerSummary?.buckets ?? []} />
        )}
        <p className="mt-3 text-xs text-kw-text-faint">
          L1-Embedding und L2-Extraction laufen realm-weit (über alle gebundenen Packs) und
          tragen daher keinen Pack — in der Pack-Verteilung unten erscheinen sie als{" "}
          <span className="font-mono">&lt;unassigned&gt;</span>. Nur pack-eigene L3/L4-Ops
          werden einem Pack zugeordnet.
        </p>
      </section>

      <section className="bg-kw-bg-elevated border border-kw-border-subtle rounded p-4">
        <h2 className="text-sm font-medium text-kw-text-muted mb-3">
          Pack-Verteilung
        </h2>
        {loading && !summary ? (
          <p className="text-sm text-kw-text-faint">Lade…</p>
        ) : (
          <StackedBreakdownChart buckets={summary?.buckets ?? []} />
        )}
      </section>

      <section className="bg-kw-bg-elevated border border-kw-border-subtle rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-kw-bg-overlay text-kw-text-muted text-xs">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Pack</th>
              <th className="px-3 py-2 text-right font-medium">USD</th>
              <th className="px-3 py-2 text-right font-medium">Calls</th>
              <th className="px-3 py-2 text-right font-medium">Tokens in</th>
              <th className="px-3 py-2 text-right font-medium">Tokens out</th>
            </tr>
          </thead>
          <tbody>
            {(summary?.buckets ?? []).map((b) => (
              <tr key={b.group_key} className="border-t border-kw-border-subtle">
                <td className="px-3 py-2 font-mono">{b.group_key}</td>
                <td className="px-3 py-2 text-right font-mono">
                  {formatUsd(b.total_cost_usd)}
                </td>
                <td className="px-3 py-2 text-right">
                  {formatInt(b.total_calls)}
                </td>
                <td className="px-3 py-2 text-right">
                  {formatInt(b.total_tokens_in)}
                </td>
                <td className="px-3 py-2 text-right">
                  {formatInt(b.total_tokens_out)}
                </td>
              </tr>
            ))}
            {(summary?.buckets ?? []).length === 0 && !loading ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-3 py-6 text-center text-kw-text-faint"
                >
                  Keine Pack-Kosten im gewählten Zeitraum.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>
    </div>
  );
}
